import React, { useState, useEffect } from 'react';
import {
    FiX, FiDownload, FiMaximize2, FiMinimize2, FiZoomIn,
    FiZoomOut, FiRotateCw, FiFile, FiImage, FiFileText,
    FiAlertCircle, FiChevronLeft, FiChevronRight
} from 'react-icons/fi';
import { supabase } from '../../config/supabase';
import { useParams } from 'react-router-dom';

const FileViewer = ({
    file,
    filename,
    originalName,
    onClose,
    files = [], // Array of files for navigation
    currentIndex = 0,
    onNavigate,
    jobId,
    company
}) => {
    const { companySlug } = useParams();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [metadata, setMetadata] = useState(null);
    const [fileUrl, setFileUrl] = useState(null);
    const [isFullscreen, setIsFullscreen] = useState(true);
    const [zoom, setZoom] = useState(100);
    const [rotation, setRotation] = useState(0);

    useEffect(() => {
        fetchFileUrl();
    }, [filename]);

    useEffect(() => {
        setError(null);
        setLoading(true);
        setZoom(100);
        setRotation(0);
    }, [filename]);

    const fetchFileUrl = async () => {
        try {
            setLoading(true);

            // The filename from the database is already the full path: companySlug/jobId/type-timestamp-filename
            // We use it directly

            let filePath;

            if (filename.startsWith(`${company}/${jobId}/`)) {
                // Already has the full path
                filePath = filename;
            } else {
                // Needs the prefix added
                filePath = `${company}/${jobId}/${filename}`;
            }

            // Since the bucket is private, we need to create a signed URL
            const { data: signedUrlData, error: signedUrlError } = await supabase.storage
                .from('job-files')
                .createSignedUrl(filePath, 3600); // URL valid for 1 hour

            if (signedUrlError) {
                console.error('Signed URL error:', signedUrlError);
                throw new Error('Failed to get file URL: ' + signedUrlError.message);
            }

            if (!signedUrlData?.signedUrl) {
                throw new Error('Failed to get file URL');
            }

            setFileUrl(signedUrlData.signedUrl);

            // Extract metadata from filename
            const extension = filename.split('.').pop()?.toLowerCase() || '';
            const category = getFileCategory(extension);

            // Get file size from storage
            // Extract the directory path and filename for listing
            const pathParts = filePath.split('/');
            const fileName = pathParts[pathParts.length - 1];
            const directory = pathParts.slice(0, -1).join('/');

            const { data: fileList, error: listError } = await supabase.storage
                .from('job-files')
                .list(directory, {
                    search: fileName
                });

            let size = 0;
            let sizeFormatted = 'Unknown size';

            if (fileList && fileList.length > 0) {
                const foundFile = fileList.find(f => f.name === fileName);
                if (foundFile?.metadata?.size) {
                    size = foundFile.metadata.size;
                    sizeFormatted = formatFileSize(size);
                }
            }

            setMetadata({
                category,
                extension,
                size,
                sizeFormatted,
                name: originalName || fileName
            });

            setLoading(false);
        } catch (err) {
            console.error('Error loading file:', err);
            setError('Failed to load file: ' + err.message);
            setLoading(false);
        }
    };

    const getFileCategory = (extension) => {
        const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg', 'webp'];
        const pdfExtensions = ['pdf'];
        const cadExtensions = ['dwg', 'dxf', 'dwf'];
        const documentExtensions = ['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'txt'];

        if (imageExtensions.includes(extension)) return 'image';
        if (pdfExtensions.includes(extension)) return 'pdf';
        if (cadExtensions.includes(extension)) return 'cad';
        if (documentExtensions.includes(extension)) return 'document';
        return 'other';
    };

    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
    };

    const handleDownload = async () => {
        try {
            // The filename is already the full path
            const filePath = `${company}/${jobId}/${filename}`

            const { data, error } = await supabase.storage
                .from('job-files')
                .download(filePath);

            if (error) throw error;

            // Create a download link
            const url = window.URL.createObjectURL(data);
            const link = document.createElement('a');
            link.href = url;
            link.download = originalName || filePath.split('/').pop();
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error('Error downloading file:', err);
            alert('Failed to download file: ' + err.message);
        }
    };

    const toggleFullscreen = () => {
        setIsFullscreen(!isFullscreen);
    };

    const handleZoomIn = () => {
        setZoom(prev => Math.min(prev + 25, 300));
    };

    const handleZoomOut = () => {
        setZoom(prev => Math.max(prev - 25, 50));
    };

    const handleRotate = () => {
        setRotation(prev => (prev + 90) % 360);
    };

    const handlePrevFile = () => {
        if (currentIndex > 0) {
            onNavigate(currentIndex - 1);
        }
    };

    const handleNextFile = () => {
        if (currentIndex < files.length - 1) {
            onNavigate(currentIndex + 1);
        }
    };

    const getFileIcon = (category) => {
        switch (category) {
            case 'image':
                return <FiImage className="w-8 h-8" />;
            case 'pdf':
                return <FiFileText className="w-8 h-8" />;
            case 'cad':
                return <FiFile className="w-8 h-8" />;
            default:
                return <FiFile className="w-8 h-8" />;
        }
    };

    const renderFileContent = () => {
        if (loading) {
            return (
                <div className="flex items-center justify-center h-full">
                    <div className="text-center">
                        <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                        <p className="text-gray-600">Loading file...</p>
                    </div>
                </div>
            );
        }

        if (error) {
            return (
                <div className="flex items-center justify-center h-full">
                    <div className="text-center text-red-500">
                        <FiAlertCircle className="w-16 h-16 mx-auto mb-4" />
                        <p className="text-lg font-medium mb-2">Error Loading File</p>
                        <p className="text-sm text-gray-600">{error}</p>
                        <button
                            onClick={fetchFileUrl}
                            className="mt-4 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                        >
                            Retry
                        </button>
                    </div>
                </div>
            );
        }

        if (!metadata || !fileUrl) return null;

        // Render based on file category
        switch (metadata.category) {
            case 'image':
                return (
                    <div className="flex items-center justify-center h-full overflow-auto p-4">
                        <img
                            src={fileUrl}
                            alt={originalName}
                            style={{
                                transform: `scale(${zoom / 100}) rotate(${rotation}deg)`,
                                transition: 'transform 0.3s ease',
                                maxWidth: '100%',
                                maxHeight: '100%',
                                objectFit: 'contain'
                            }}
                            className="shadow-2xl"
                            onError={(e) => {
                                console.error('Image load error');
                                setError('Failed to load image');
                            }}
                        />
                    </div>
                );

            case 'pdf':
                return (
                    <div className="h-full w-full">
                        <iframe
                            src={fileUrl}
                            className="w-full h-full border-0"
                            title={originalName}
                            onError={(e) => {
                                console.error('PDF load error');
                                setError('Failed to load PDF');
                            }}
                        />
                    </div>
                );

            case 'cad':
            case 'document':
                return (
                    <div className="flex flex-col items-center justify-center h-full p-8">
                        <div className="text-center bg-white rounded-2xl shadow-xl p-12 max-w-md">
                            <div className="w-24 h-24 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-6 text-white">
                                {getFileIcon(metadata.category)}
                            </div>
                            <h3 className="text-2xl font-bold text-gray-900 mb-2">
                                {originalName}
                            </h3>
                            <p className="text-gray-600 mb-6">
                                {metadata.sizeFormatted} • {metadata.extension.toUpperCase()}
                            </p>
                            <div className="space-y-3">
                                <button
                                    onClick={handleDownload}
                                    className="w-full py-3 px-6 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg hover:from-blue-600 hover:to-blue-700 transition-all shadow-md hover:shadow-lg font-medium flex items-center justify-center gap-2"
                                >
                                    <FiDownload className="w-5 h-5" />
                                    Download File
                                </button>
                                <p className="text-sm text-gray-500">
                                    This file type cannot be previewed in the browser
                                </p>
                            </div>
                        </div>
                    </div>
                );

            default:
                return (
                    <div className="flex items-center justify-center h-full">
                        <div className="text-center">
                            <FiFile className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                            <p className="text-gray-600 mb-4">File preview not available</p>
                            <button
                                onClick={handleDownload}
                                className="py-2 px-6 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors font-medium flex items-center justify-center gap-2 mx-auto"
                            >
                                <FiDownload className="w-4 h-4" />
                                Download File
                            </button>
                        </div>
                    </div>
                );
        }
    };

    // Keyboard shortcuts
    useEffect(() => {
        const handleKeyDown = (e) => {
            switch (e.key) {
                case 'Escape':
                    onClose();
                    break;
                case 'ArrowLeft':
                    handlePrevFile();
                    break;
                case 'ArrowRight':
                    handleNextFile();
                    break;
                case '+':
                case '=':
                    if (metadata?.category === 'image') handleZoomIn();
                    break;
                case '-':
                case '_':
                    if (metadata?.category === 'image') handleZoomOut();
                    break;
                default:
                    break;
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [metadata, currentIndex, files.length]);

    return (
        <div className={`fixed inset-0 z-50 bg-black bg-opacity-95 flex flex-col !mt-0`}>
            {/* Header */}
            <div className="bg-zinc-900 border-b border-gray-800 px-6 py-4 flex items-center justify-between">
                <div className="flex items-center gap-4 flex-1 min-w-0">
                    {/* Navigation arrows for multiple files */}
                    {files.length > 1 && (
                        <div className="flex items-center gap-2">
                            <button
                                onClick={handlePrevFile}
                                disabled={currentIndex === 0}
                                className="p-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                                title="Previous file"
                            >
                                <FiChevronLeft className="w-5 h-5" />
                            </button>
                            <span className="text-sm text-gray-400">
                                {currentIndex + 1} / {files.length}
                            </span>
                            <button
                                onClick={handleNextFile}
                                disabled={currentIndex === files.length - 1}
                                className="p-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                                title="Next file"
                            >
                                <FiChevronRight className="w-5 h-5" />
                            </button>
                        </div>
                    )}

                    <div className="flex-1 min-w-0">
                        <h2 className="text-white font-semibold text-lg truncate">
                            {originalName || filename.split('/').pop()}
                        </h2>
                        {metadata && (
                            <p className="text-gray-400 text-sm">
                                {metadata.sizeFormatted} • {metadata.extension.toUpperCase()}
                            </p>
                        )}
                    </div>
                </div>

                {/* Controls */}
                <div className="flex items-center gap-2 ml-4">
                    {/* Image-specific controls */}
                    {metadata?.category === 'image' && (
                        <>
                            <button
                                onClick={handleZoomOut}
                                className="p-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors"
                                title="Zoom out"
                            >
                                <FiZoomOut className="w-5 h-5" />
                            </button>
                            <span className="text-gray-400 text-sm min-w-[60px] text-center">
                                {zoom}%
                            </span>
                            <button
                                onClick={handleZoomIn}
                                className="p-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors"
                                title="Zoom in"
                            >
                                <FiZoomIn className="w-5 h-5" />
                            </button>
                            <button
                                onClick={handleRotate}
                                className="p-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors"
                                title="Rotate"
                            >
                                <FiRotateCw className="w-5 h-5" />
                            </button>
                            <div className="w-px h-6 bg-gray-700 mx-2"></div>
                        </>
                    )}

                    <button
                        onClick={handleDownload}
                        className="p-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors"
                        title="Download"
                    >
                        <FiDownload className="w-5 h-5" />
                    </button>

                    <div className="w-px h-6 bg-gray-700 mx-2"></div>

                    <button
                        onClick={onClose}
                        className="p-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors"
                        title="Close"
                    >
                        <FiX className="w-5 h-5" />
                    </button>
                </div>
            </div>

            {/* Content Area */}
            <div className="flex-1 overflow-hidden bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
                {renderFileContent()}
            </div>

            {/* Keyboard shortcuts hint */}
            <div className="bg-gray-900 border-t border-gray-800 px-6 py-2">
                <div className="flex items-center justify-center gap-6 text-xs text-gray-500">
                    <span><kbd className="px-2 py-1 bg-gray-800 rounded">ESC</kbd> Close</span>
                    {files.length > 1 && (
                        <>
                            <span><kbd className="px-2 py-1 bg-gray-800 rounded">←</kbd> Previous</span>
                            <span><kbd className="px-2 py-1 bg-gray-800 rounded">→</kbd> Next</span>
                        </>
                    )}
                    {metadata?.category === 'image' && (
                        <>
                            <span><kbd className="px-2 py-1 bg-gray-800 rounded">+</kbd> Zoom in</span>
                            <span><kbd className="px-2 py-1 bg-gray-800 rounded">-</kbd> Zoom out</span>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default FileViewer;